import * as jsPdf from 'jspdf';
import * as moment from 'moment-timezone';
import { EInputMask } from 'src/Components/EInput';
import { ITest } from 'src/Interfaces/TestInterface';
import { IWell } from 'src/Interfaces/WellInterface';
import { equalGrid } from '../MudReport/DrawReportMud';
import { fillEmptyRows } from '../MudReport/PopDataMud';
import { textArrayInBoxes } from '../PopulateDataMethods';
import * as MM from './SpecMilsProductInventory';

const mask = new EInputMask();
export const createProductInventorySnapshotReport = (test: ITest, well: IWell, logoBlob: HTMLImageElement, volumeReport: jsPdf) => {
    volumeReport.setFillColor(MM.HEADER_COLOR);
    popReport(test, well, logoBlob, volumeReport);
};

const popReport = (test: ITest, well: IWell, logoBlob: HTMLImageElement, volumeReport: jsPdf) => {
    drawHeader(test, well, logoBlob, volumeReport);
    drawTotals(test, volumeReport);
    let count = drawProductsTable(test, 0, volumeReport);
    while (count < test.productInventorys.length)
    {
        volumeReport.addPage();
        drawHeader(test, well, logoBlob, volumeReport);
        count = drawProductsTable(test, count, volumeReport);
    }
};

const drawHeader = (test: ITest, well: IWell, logoBlob: HTMLImageElement, volumeReport: jsPdf) => {
    volumeReport.addImage(logoBlob, 'PNG', MM.LOGO_LEFT, MM.LOGO_TOP, MM.LOGO_WIDTH, MM.LOGO_HEIGHT);
    popOperatorContractorDepth(test, well, volumeReport);
};

const popOperatorContractorDepth = (test: ITest, well: IWell, volumeReport: jsPdf) => {
    const boldText = [
        [`Operator: ${well.operator.operatorName}`, `Contractor: ${well.contractor.contractorName}`],
    ];
    fillEmptyRows(boldText, 6);
    const legal = well.state === 'TX'
        ? 'Section ' + well.shlSection + ', Block ' + well.shlBlock + well.shlBlockDirection + ', Township ' + well.shlTownship + well.shlTownshipDirection
        : 'Section ' + well.shlSection + ', Range ' + well.shlRange + well.shlRangeDirection + ', Township ' + well.shlTownship + well.shlTownshipDirection;
    const normalText = [
        [undefined],
        [`Well Name: ${well.wellName}`, `Rig #: ${well.rigNumber}`],
        [`API: ${well.apiNumber}`, `Spud Date: ${well.spudDate}`],
        [`Legal: ${legal}`, `Test Date: ${moment.unix(test.testDate).tz(well.timeZoneName).format("MMM DD YYYY, h:mm a")}`],
        [`County: ${well.countyParish}`, undefined],
        [`State: ${well.state}`, undefined]
    ];
    volumeReport.setFontSize(MM.HEADER_1_SIZE);
    volumeReport.setFontStyle('bold');
    textArrayInBoxes(boldText, MM.OPERATOR_CONTRACTOR_LEFT, MM.OPERATOR_CONTRACTOR_TOP, MM.OPERATOR_CONTRACTOR_WIDTH, MM.OPERTAOR_CONTRACTOR_HEIGHT, 'left', volumeReport);
    volumeReport.setFontStyle('normal');
    textArrayInBoxes(normalText, MM.OPERATOR_CONTRACTOR_LEFT, MM.OPERATOR_CONTRACTOR_TOP, MM.OPERATOR_CONTRACTOR_WIDTH, MM.OPERTAOR_CONTRACTOR_HEIGHT, 'left', volumeReport);

    const depthArr = [
        [undefined],
        ['MD:', test.depthMD],
        ['TVD:', test.depthTVD],
        [undefined],
    ];

    volumeReport.setFontStyle('bold');
    textArrayInBoxes(
        depthArr,
        MM.DEPTH_LEFT,
        MM.DEPTH_TOP,
        MM.DEPTH_WIDTH,
        MM.DEPTH_HEIGHT,
        'left',
        volumeReport
    );

};

const drawTotals = (test: ITest, productReport: jsPdf) => {
    const headerArr = [[
        'Product Total',
        'Product Tax',
        'EBP Total',
        'EBP Tax',
        'Total',
        'Cumulative',
    ]];

    const totalsArr = [
        [undefined],
        [
            mask.currency(test.inventoryTotal.productTotal),
            mask.currency(test.inventoryTotal.productTax),
            mask.currency(test.inventoryTotal.otherCosts),
            mask.currency(test.inventoryTotal.otherCostTax),
            mask.currency(test.inventoryTotal.dailyTotal),
            mask.currency(test.inventoryTotal.cumulativeTotal),
        ]
    ];
    fillEmptyRows(headerArr, 2);
    productReport.setFillColor(MM.HEADER_COLOR);
    productReport.rect(MM.TOTALS_LEFT, MM.TOTALS_TOP, MM.TOTALS_WIDTH, MM.TOTALS_HEIGHT / 2, 'f');
    productReport.rect(MM.TOTALS_LEFT, MM.TOTALS_TOP, MM.TOTALS_WIDTH, MM.TOTALS_HEIGHT);
    textArrayInBoxes(headerArr, MM.TOTALS_LEFT, MM.TOTALS_TOP, MM.TOTALS_WIDTH, MM.TOTALS_HEIGHT, 'left', productReport);
    equalGrid(MM.TOTALS_LEFT, MM.TOTALS_TOP, MM.TOTALS_WIDTH, MM.TOTALS_HEIGHT, MM.TOTALS_ROWS, MM.TOTALS_COLUMNS, productReport);
    productReport.setFontStyle('normal');
    textArrayInBoxes(totalsArr, MM.TOTALS_LEFT, MM.TOTALS_TOP, MM.TOTALS_WIDTH, MM.TOTALS_HEIGHT, 'center', productReport);

};

const drawProductsTable = (test: ITest, startIndex:number, productReport: jsPdf) => {
    const headerArr = [[
        'Product',
        'Size',
        'Unit',
        'Price',
        'Starting',
        'Adjusted',
        'Received',
        'Returned',
        'Closing',
        'Used',
        'Total',
    ]];
    const dataArr = [[undefined]] as any;
    let i = startIndex;
    const numRows = startIndex === 0 ? MM.PRODUCTS_ROWS : MM.DATA_ROWS;
    const piArr = test.productInventorys;
    
    for (;i < piArr.length && i - startIndex < numRows; i++) {
        const ip = piArr[i];
        const tempRow = [
            ip.product.name,
            ip.product.size,
            ip.product.unit,
            mask.currency(ip.priceUsed),
            ip.startingAmount,
            ip.adjustedStartingAmount,
            ip.amountReceived,
            ip.amountReturned,
            ip.closingAmount,
            ip.amountUsed,
            mask.currency(ip.totalCost),
        ];
        dataArr.push(tempRow);
    }
    fillEmptyRows(headerArr, numRows);
    fillEmptyRows(dataArr, numRows);
    productReport.setFillColor(MM.HEADER_COLOR);
    const tempTop = startIndex === 0 ? MM.PRODUCTS_TOP : MM.DATA_TOP;
    const tempHeight = startIndex === 0 ? MM.PRODUCTS_HEIGHT : MM.DATA_HEIGHT;
    productReport.rect(MM.PRODUCTS_LEFT, tempTop, MM.PRODUCTS_WIDTH, tempHeight / numRows, 'f');
    productReport.rect(MM.PRODUCTS_LEFT, tempTop, MM.PRODUCTS_WIDTH, tempHeight);
    productReport.setFontStyle('bold');
    textArrayInBoxes(headerArr, MM.PRODUCTS_LEFT, tempTop, MM.PRODUCTS_WIDTH, tempHeight, 'left', productReport);
    equalGrid(MM.PRODUCTS_LEFT, tempTop, MM.PRODUCTS_WIDTH, tempHeight, numRows, MM.PRODUCTS_COLUMNS, productReport);
    productReport.setFontStyle('normal');
    textArrayInBoxes(dataArr, MM.PRODUCTS_LEFT, tempTop, MM.PRODUCTS_WIDTH, tempHeight, 'left', productReport);
    return i;
};


// WEBPACK FOOTER //
// ./src/Services/Pdf/ProductInventorySnapshot/DrawReportProductInventory.ts